import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ArrowLeft, Dumbbell, Calendar, Clock, Flame, Target,
  CheckCircle, XCircle, TrendingUp, Trophy, Users, User,
  ChevronRight, Activity, Zap, Moon, Settings, LogOut,
  BarChart3, CalendarDays
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import axios from 'axios';
import { toast } from 'sonner';
import { format, parseISO, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay, subDays } from 'date-fns';
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip, BarChart, Bar } from 'recharts';

const FamilyWorkoutsPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [athletes, setAthletes] = useState([]);
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [athleteData, setAthleteData] = useState({});
  const [activeTab, setActiveTab] = useState('today');

  useEffect(() => {
    fetchAthletes();
  }, []);

  useEffect(() => {
    if (selectedAthlete) {
      fetchAthleteWorkoutData(selectedAthlete.user_id);
    }
  }, [selectedAthlete]);

  const fetchAthletes = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/family/athletes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const athletesList = response.data.athletes || [];
      setAthletes(athletesList);
      if (athletesList.length > 0) {
        setSelectedAthlete(athletesList[0]);
      }
    } catch (error) {
      console.error('Failed to fetch athletes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAthleteWorkoutData = async (athleteId) => {
    try {
      const token = localStorage.getItem('aura_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [workoutsRes, recoveryRes, scheduleRes, badgesRes] = await Promise.all([
        axios.get(`${API}/supporter/athlete/${athleteId}/workouts`, { headers }).catch(() => ({ data: { workouts: [] } })),
        axios.get(`${API}/supporter/athlete/${athleteId}/recovery`, { headers }).catch(() => ({ data: { logs: [] } })),
        axios.get(`${API}/supporter/athlete/${athleteId}/schedule`, { headers }).catch(() => ({ data: { events: [] } })),
        axios.get(`${API}/supporter/athlete/${athleteId}/badges`, { headers }).catch(() => ({ data: { badges: [], total_points: 0 } }))
      ]);

      setAthleteData(prev => ({
        ...prev,
        [athleteId]: {
          workouts: workoutsRes.data.workouts || [],
          recovery: recoveryRes.data.logs || [],
          schedule: scheduleRes.data.events || [],
          badges: badgesRes.data
        }
      }));
    } catch (error) {
      console.error('Failed to fetch athlete data:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  const currentData = selectedAthlete ? athleteData[selectedAthlete.user_id] : null;

  // Get today's scheduled workouts
  const getTodayWorkouts = () => {
    if (!currentData?.schedule) return [];
    const today = new Date();
    return currentData.schedule.filter(event => {
      const eventDate = new Date(event.start_time);
      return isSameDay(eventDate, today) && 
        ['workout', 'practice', 'personal_training', 'conditioning', 'game'].includes(event.event_type);
    });
  };

  // Get recent completed workouts (last 7 days)
  const getRecentWorkouts = () => {
    if (!currentData?.workouts) return [];
    const sevenDaysAgo = subDays(new Date(), 7);
    return currentData.workouts.filter(w => new Date(w.created_at) >= sevenDaysAgo);
  };

  // Get this week's workout data for chart
  const getWeeklyChartData = () => {
    const start = startOfWeek(new Date());
    const end = endOfWeek(new Date());
    const days = eachDayOfInterval({ start, end });
    
    return days.map(day => {
      const dayWorkouts = currentData?.workouts?.filter(w => 
        isSameDay(new Date(w.created_at), day)
      ) || [];
      
      const totalMinutes = dayWorkouts.reduce((sum, w) => sum + (w.duration_minutes || 0), 0);
      
      return {
        day: format(day, 'EEE'),
        workouts: dayWorkouts.length,
        minutes: totalMinutes,
        isToday: isSameDay(day, new Date())
      };
    });
  };

  // Calculate weekly progress
  const getWeeklyStats = () => {
    const recentWorkouts = getRecentWorkouts();
    const totalWorkouts = recentWorkouts.length;
    const totalMinutes = recentWorkouts.reduce((sum, w) => sum + (w.duration_minutes || 0), 0);
    const avgIntensity = recentWorkouts.length > 0
      ? recentWorkouts.reduce((sum, w) => {
          const intensityMap = { low: 1, moderate: 2, high: 3 };
          return sum + (intensityMap[w.intensity] || 2);
        }, 0) / recentWorkouts.length
      : 0;
    
    return { totalWorkouts, totalMinutes, avgIntensity };
  };

  // Get workout completion rate
  const getCompletionRate = () => {
    const todayScheduled = getTodayWorkouts();
    const todayCompleted = currentData?.workouts?.filter(w => 
      isSameDay(new Date(w.created_at), new Date())
    ) || [];
    
    if (todayScheduled.length === 0) return null;
    return Math.round((todayCompleted.length / todayScheduled.length) * 100);
  };

  const todayWorkouts = getTodayWorkouts();
  const recentWorkouts = getRecentWorkouts();
  const weeklyData = getWeeklyChartData();
  const weeklyStats = getWeeklyStats();
  const completionRate = getCompletionRate();
  const latestRecovery = currentData?.recovery?.[0];

  const getIntensityColor = (intensity) => {
    switch (intensity) {
      case 'high': return 'text-red-400 bg-red-500/20';
      case 'moderate': return 'text-yellow-400 bg-yellow-500/20';
      case 'low': return 'text-green-400 bg-green-500/20';
      default: return 'text-aura-text-muted bg-white/10';
    }
  };

  const getEventTypeColor = (type) => {
    const colors = {
      workout: 'bg-aura-secondary',
      practice: 'bg-aura-primary',
      personal_training: 'bg-purple-500',
      conditioning: 'bg-orange-500',
      game: 'bg-green-500'
    };
    return colors[type] || 'bg-gray-500';
  };

  return (
    <div className="min-h-screen bg-aura-bg">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-aura-bg/95 backdrop-blur-lg border-b border-white/5">
        <div className="flex items-center justify-between h-14 px-4 max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/supporter')}
              className="p-2 text-aura-text-muted hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="w-8 h-8 bg-aura-secondary rounded-sm flex items-center justify-center">
              <span className="font-heading font-black text-white text-lg">A</span>
            </div>
            <span className="font-heading font-bold text-lg text-white tracking-tight">AURA</span>
            <span className="text-xs text-aura-text-muted uppercase tracking-wider hidden sm:block">Workouts</span>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 p-2 rounded-lg hover:bg-white/5 transition-colors">
                <div className="w-8 h-8 rounded-full bg-aura-secondary flex items-center justify-center">
                  {user?.picture ? (
                    <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                  ) : (
                    <span className="font-heading font-bold text-white text-sm">
                      {user?.name?.charAt(0)?.toUpperCase() || 'S'}
                    </span>
                  )}
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-aura-surface border-white/10">
              <div className="px-3 py-2">
                <p className="font-medium text-white">{user?.name}</p>
                <p className="text-sm text-aura-text-muted">{user?.email}</p>
              </div>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem asChild>
                <Link to="/supporter" className="flex items-center gap-2 text-white cursor-pointer">
                  <Users className="w-4 h-4" />
                  Family Dashboard
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/settings" className="flex items-center gap-2 text-white cursor-pointer">
                  <Settings className="w-4 h-4" />
                  Settings
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem 
                onClick={handleLogout}
                className="flex items-center gap-2 text-aura-destructive cursor-pointer"
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 py-6 max-w-7xl mx-auto space-y-6">
        {/* Page Header */}
        <div>
          <h1 className="font-heading font-bold text-2xl sm:text-3xl text-white uppercase tracking-tight">
            Athlete Workouts
          </h1>
          <p className="text-aura-text-muted mt-1">
            Track daily activities and progress
          </p>
        </div>

        {/* Athlete Selector */}
        {athletes.length > 1 && (
          <div className="flex items-center gap-3 overflow-x-auto pb-2">
            {athletes.map((athlete) => (
              <button
                key={athlete.user_id}
                onClick={() => setSelectedAthlete(athlete)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  selectedAthlete?.user_id === athlete.user_id
                    ? 'bg-aura-primary text-white'
                    : 'bg-white/10 text-aura-text-muted hover:bg-white/20'
                }`}
              >
                <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                  {athlete.picture ? (
                    <img src={athlete.picture} alt="" className="w-6 h-6 rounded-full" />
                  ) : (
                    <span className="text-xs font-bold">{athlete.name?.charAt(0)}</span>
                  )}
                </div>
                {athlete.name}
              </button>
            ))}
          </div>
        )}

        {selectedAthlete && (
          <>
            {/* Quick Stats Row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Card className="bg-aura-primary/20 border-0">
                <CardContent className="p-4 text-center">
                  <Dumbbell className="w-6 h-6 text-aura-primary mx-auto mb-2" />
                  <p className="font-heading font-bold text-3xl text-aura-primary">
                    {weeklyStats.totalWorkouts}
                  </p>
                  <p className="text-xs text-aura-text-muted uppercase tracking-wider">This Week</p>
                </CardContent>
              </Card>

              <Card className="bg-orange-500/20 border-0">
                <CardContent className="p-4 text-center">
                  <Clock className="w-6 h-6 text-orange-400 mx-auto mb-2" />
                  <p className="font-heading font-bold text-3xl text-orange-400">
                    {weeklyStats.totalMinutes}
                  </p>
                  <p className="text-xs text-aura-text-muted uppercase tracking-wider">Minutes</p>
                </CardContent>
              </Card>

              <Card className="bg-cyan-500/20 border-0">
                <CardContent className="p-4 text-center">
                  <Moon className="w-6 h-6 text-cyan-400 mx-auto mb-2" />
                  <p className="font-heading font-bold text-3xl text-cyan-400">
                    {latestRecovery?.recovery_score || '--'}
                  </p>
                  <p className="text-xs text-aura-text-muted uppercase tracking-wider">Recovery</p>
                </CardContent>
              </Card>

              <Card className="bg-yellow-500/20 border-0">
                <CardContent className="p-4 text-center">
                  <Trophy className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
                  <p className="font-heading font-bold text-3xl text-yellow-400">
                    {currentData?.badges?.total_points || 0}
                  </p>
                  <p className="text-xs text-aura-text-muted uppercase tracking-wider">Aura Score</p>
                </CardContent>
              </Card>
            </div>

            {/* Tabs for Today / This Week / Progress */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="bg-white/5 border border-white/10 p-1 w-full sm:w-auto">
                <TabsTrigger value="today" className="data-[state=active]:bg-aura-primary data-[state=active]:text-white">
                  <CalendarDays className="w-4 h-4 mr-2" />
                  Today
                </TabsTrigger>
                <TabsTrigger value="week" className="data-[state=active]:bg-aura-primary data-[state=active]:text-white">
                  <BarChart3 className="w-4 h-4 mr-2" />
                  This Week
                </TabsTrigger>
                <TabsTrigger value="progress" className="data-[state=active]:bg-aura-primary data-[state=active]:text-white">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Progress
                </TabsTrigger>
              </TabsList>

              {/* Today's Activities Tab */}
              <TabsContent value="today" className="mt-6 space-y-6">
                {/* Today's Completion Status */}
                {completionRate !== null && (
                  <Card className={`border-0 ${completionRate === 100 ? 'bg-green-500/20' : completionRate > 0 ? 'bg-yellow-500/20' : 'bg-red-500/20'}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {completionRate === 100 ? (
                            <CheckCircle className="w-5 h-5 text-green-400" />
                          ) : (
                            <Target className="w-5 h-5 text-yellow-400" />
                          )}
                          <span className="font-heading font-bold text-white uppercase">Today's Progress</span>
                        </div>
                        <span className="font-heading font-bold text-2xl text-white">{completionRate}%</span>
                      </div>
                      <Progress value={completionRate} className="h-2" />
                      <p className="text-sm text-aura-text-muted mt-2">
                        {completionRate === 100 
                          ? `${selectedAthlete.name} completed all scheduled activities!` 
                          : `${todayWorkouts.length} activities scheduled for today`}
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Today's Scheduled Activities */}
                <Card className="bg-aura-surface border-white/10">
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-aura-primary" />
                      Today's Schedule
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {todayWorkouts.length > 0 ? (
                      <div className="space-y-3">
                        {todayWorkouts.map((event, idx) => {
                          const isCompleted = currentData?.workouts?.some(w => 
                            isSameDay(new Date(w.created_at), new Date()) &&
                            w.name?.toLowerCase().includes(event.title?.toLowerCase().split(' ')[0])
                          );
                          return (
                            <div key={idx} className={`flex items-center gap-3 p-3 rounded-lg ${isCompleted ? 'bg-green-500/10 border border-green-500/30' : 'bg-white/5'}`}>
                              <div className={`w-10 h-10 rounded-lg ${getEventTypeColor(event.event_type)} bg-opacity-20 flex items-center justify-center`}>
                                <Dumbbell className="w-5 h-5 text-white" />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="font-medium text-white">{event.title}</p>
                                  {isCompleted && (
                                    <Badge className="bg-green-500/20 text-green-400 border-0 text-xs">
                                      <CheckCircle className="w-3 h-3 mr-1" />
                                      Done
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-aura-text-muted">
                                  {format(parseISO(event.start_time), 'h:mm a')}
                                  {event.location ? ` • ${event.location}` : ''}
                                </p>
                              </div>
                              <Badge variant="outline" className="text-xs capitalize">
                                {event.event_type?.replace('_', ' ')}
                              </Badge>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Calendar className="w-12 h-12 text-aura-text-subtle mx-auto mb-3" />
                        <p className="text-aura-text-muted">No activities scheduled for today</p>
                        <Link to="/family/schedule">
                          <Button className="mt-3 bg-aura-primary hover:bg-aura-primary-hover text-white">
                            Schedule Activity
                          </Button>
                        </Link>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Completed Workouts Today */}
                {currentData?.workouts?.filter(w => isSameDay(new Date(w.created_at), new Date())).length > 0 && (
                  <Card className="bg-aura-surface border-white/10">
                    <CardHeader className="pb-2">
                      <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-400" />
                        Completed Today
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {currentData.workouts
                          .filter(w => isSameDay(new Date(w.created_at), new Date()))
                          .map((workout, idx) => (
                            <div key={idx} className="flex items-center gap-3 p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                                <Dumbbell className="w-5 h-5 text-green-400" />
                              </div>
                              <div className="flex-1">
                                <p className="font-medium text-white">{workout.name}</p>
                                <p className="text-sm text-aura-text-muted">
                                  {workout.duration_minutes} min • {workout.exercises?.length || 0} exercises
                                </p>
                              </div>
                              <Badge className={`text-xs ${getIntensityColor(workout.intensity)}`}>
                                {workout.intensity || 'moderate'}
                              </Badge>
                            </div>
                          ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* This Week Tab */}
              <TabsContent value="week" className="mt-6 space-y-6">
                {/* Weekly Activity Chart */}
                <Card className="bg-aura-surface border-white/10">
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-aura-secondary" />
                      Weekly Activity
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={weeklyData}>
                          <XAxis dataKey="day" stroke="#71717A" fontSize={12} />
                          <YAxis stroke="#71717A" fontSize={12} />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: '#18181B', 
                              border: '1px solid rgba(255,255,255,0.1)',
                              borderRadius: '8px'
                            }}
                            labelStyle={{ color: '#fff' }}
                          />
                          <Bar dataKey="minutes" fill="#F97316" radius={[4, 4, 0, 0]} name="Minutes" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* All Workouts This Week */}
                <Card className="bg-aura-surface border-white/10">
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
                      <Dumbbell className="w-5 h-5 text-aura-primary" />
                      Workouts This Week ({recentWorkouts.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {recentWorkouts.length > 0 ? (
                      <div className="space-y-3">
                        {recentWorkouts.map((workout, idx) => (
                          <div key={idx} className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                            <div className="w-10 h-10 rounded-lg bg-aura-primary/20 flex items-center justify-center">
                              <Dumbbell className="w-5 h-5 text-aura-primary" />
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-white">{workout.name}</p>
                              <p className="text-sm text-aura-text-muted">
                                {format(new Date(workout.created_at), 'EEE, MMM d')} • {workout.duration_minutes} min
                              </p>
                            </div>
                            <div className="text-right">
                              <Badge className={`text-xs ${getIntensityColor(workout.intensity)}`}>
                                {workout.intensity || 'moderate'}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Dumbbell className="w-12 h-12 text-aura-text-subtle mx-auto mb-3" />
                        <p className="text-aura-text-muted">No workouts logged this week</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Progress Tab */}
              <TabsContent value="progress" className="mt-6 space-y-6">
                {/* Recovery Trend */}
                {currentData?.recovery?.length > 0 && (
                  <Card className="bg-aura-surface border-white/10">
                    <CardHeader className="pb-2">
                      <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
                        <Activity className="w-5 h-5 text-cyan-400" />
                        Recovery Trend
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-48">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={currentData.recovery.slice(0, 7).reverse().map((log, idx) => ({
                            day: `Day ${idx + 1}`,
                            score: log.recovery_score,
                            sleep: log.sleep_hours
                          }))}>
                            <defs>
                              <linearGradient id="recoveryGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#06B6D4" stopOpacity={0.3}/>
                                <stop offset="95%" stopColor="#06B6D4" stopOpacity={0}/>
                              </linearGradient>
                            </defs>
                            <XAxis dataKey="day" stroke="#71717A" fontSize={12} />
                            <YAxis stroke="#71717A" fontSize={12} domain={[0, 100]} />
                            <Tooltip 
                              contentStyle={{ 
                                backgroundColor: '#18181B', 
                                border: '1px solid rgba(255,255,255,0.1)',
                                borderRadius: '8px'
                              }}
                              labelStyle={{ color: '#fff' }}
                            />
                            <Area 
                              type="monotone" 
                              dataKey="score" 
                              stroke="#06B6D4" 
                              strokeWidth={2}
                              fill="url(#recoveryGradient)" 
                              name="Recovery Score"
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Achievements */}
                {currentData?.badges?.badges?.filter(b => b.earned)?.length > 0 && (
                  <Card className="bg-aura-surface border-white/10">
                    <CardHeader className="pb-2">
                      <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
                        <Trophy className="w-5 h-5 text-yellow-400" />
                        Recent Achievements
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-4">
                        {currentData.badges.badges
                          .filter(b => b.earned)
                          .slice(0, 6)
                          .map((badge, idx) => {
                            const tierColors = {
                              bronze: 'bg-amber-600',
                              silver: 'bg-slate-400',
                              gold: 'bg-yellow-400'
                            };
                            return (
                              <div key={idx} className="text-center">
                                <div className={`w-12 h-12 mx-auto rounded-full ${tierColors[badge.tier] || 'bg-aura-primary'} flex items-center justify-center mb-2`}>
                                  <Trophy className="w-6 h-6 text-white" />
                                </div>
                                <p className="text-xs text-white font-medium truncate">{badge.name}</p>
                                <p className="text-xs text-aura-text-muted capitalize">{badge.tier}</p>
                              </div>
                            );
                          })}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Summary Stats */}
                <Card className="bg-gradient-to-r from-aura-primary/10 to-aura-secondary/10 border-aura-primary/30">
                  <CardContent className="p-6">
                    <h3 className="font-heading font-bold text-lg text-white uppercase tracking-wider mb-4">
                      {selectedAthlete.name}'s Stats
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div className="text-center p-3 bg-white/5 rounded-lg">
                        <p className="font-heading font-bold text-2xl text-aura-primary">{weeklyStats.totalWorkouts}</p>
                        <p className="text-xs text-aura-text-muted">Workouts/Week</p>
                      </div>
                      <div className="text-center p-3 bg-white/5 rounded-lg">
                        <p className="font-heading font-bold text-2xl text-orange-400">{weeklyStats.totalMinutes}</p>
                        <p className="text-xs text-aura-text-muted">Minutes/Week</p>
                      </div>
                      <div className="text-center p-3 bg-white/5 rounded-lg">
                        <p className="font-heading font-bold text-2xl text-yellow-400">
                          {weeklyStats.avgIntensity > 0 ? ['Low', 'Moderate', 'High'][Math.round(weeklyStats.avgIntensity) - 1] : '--'}
                        </p>
                        <p className="text-xs text-aura-text-muted">Avg Intensity</p>
                      </div>
                      <div className="text-center p-3 bg-white/5 rounded-lg">
                        <p className="font-heading font-bold text-2xl text-cyan-400">{selectedAthlete.streak || 0}</p>
                        <p className="text-xs text-aura-text-muted">Day Streak</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </>
        )}

        {/* No Athletes State */}
        {!loading && athletes.length === 0 && (
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-8 text-center">
              <Users className="w-12 h-12 text-aura-text-subtle mx-auto mb-4" />
              <h3 className="font-heading font-bold text-lg text-white mb-2">No Athletes Linked</h3>
              <p className="text-aura-text-muted mb-4">
                Link your athletes to view their workout progress
              </p>
              <Link to="/supporter">
                <Button className="bg-aura-secondary hover:bg-aura-secondary/90 text-white">
                  Add Athlete
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default FamilyWorkoutsPage;
